/* eslint-disable @typescript-eslint/naming-convention */
import type { TranslationTag } from '@ps/web-core-intl';
import type { MatchInfo, Reference } from '@ps/web-martech-cms-types';
import { Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import { format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';

import { CMSHTML } from '@swf/cms';
import { select } from '@swf/core/store';

import styles from './MatchDetails.scss';

interface MatchDetailsProps {
  dataRef: Reference[];
  previewTranslation: TranslationTag;
  reportTranslation: TranslationTag;
}

const MatchDetails: React.FC<MatchDetailsProps> = ({
  dataRef,
  previewTranslation,
  reportTranslation
}) => {
  const cmsContent = useSelector(select.cms.getContent);

  return (
    <>
      {dataRef.map(({ uid }, index) => {
        const { info_type, game_date, content } = cmsContent[uid] as MatchInfo;

        if (!content) {
          return null;
        }

        const type =
          info_type === 'postgame' ? (
            <Translation {...reportTranslation} />
          ) : (
            <Translation {...previewTranslation} />
          );
        const gameDate = format(new Date(game_date), 'LLLL d');
        const hashGameDate = format(new Date(game_date), 'yyyy-MM-dd');
        return (
          <div key={index} id={`${info_type}-${hashGameDate}`}>
            <p className={styles.title}>
              {type} | {gameDate}
            </p>
            {info_type === 'pregame' && (
              <>
                <CMSHTML content={content.main_title as string} />
                <CMSHTML content={content.main_headline as string} />
                <CMSHTML content={content.intro_segment as string} />
                <CMSHTML content={content.home_team as string} />
                <CMSHTML content={content.away_team as string} />
                <CMSHTML content={content.generated_prediction as string} />
                <CMSHTML content={content.conclusion_segment as string} />
                <StarsInfo type="info">
                  <CMSHTML content={content.did_you_know as string} />
                </StarsInfo>
                <CMSHTML content={content.outro_segment as string} />
              </>
            )}
            {info_type === 'postgame' && (
              <>
                <CMSHTML
                  className={styles.scorersSegment}
                  content={content.scorers_segment as string}
                />
                <CMSHTML className={styles.statsTable} content={content.stats_table as string} />
                <CMSHTML content={content.headline as string} />
                <CMSHTML content={content.game_summary as string} />
                <CMSHTML content={content.common_stats as string} />
                <CMSHTML content={content.notable_stats as string} />
                <CMSHTML content={content.game_outcome_consequence as string} />
                <CMSHTML
                  className={styles.topPerformers}
                  content={content.top_performers as string}
                />
                <CMSHTML content={content.next_up as string} />
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default MatchDetails;
